import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const ImpersonationContext = createContext(null);
const STORAGE_KEY = 'impersonate-as';
const QA_KEY = 'impersonate-qa-show-hidden';

function readStored() {
  try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY)); } catch { return null; }
}

// The owner may act while viewing as someone (Lindsay tests flows from staff
// views); everyone else who can View As (`view_as` toggle) stays read-only. Read
// from the real stored session — impersonation never touches it.
function realIsOwner() {
  try { return JSON.parse(localStorage.getItem('staff-session'))?.opsRole === 'owner'; } catch { return false; }
}

function readQa() {
  try { return sessionStorage.getItem(QA_KEY) === '1'; } catch { return false; }
}

export function ImpersonationProvider({ children }) {
  const [impersonatedAs, setImpersonatedAs] = useState(readStored);
  const [qaShowHidden, setQaShowHiddenState] = useState(readQa);

  const startImpersonating = useCallback((emp) => {
    if (!emp || !emp.employeeId) return;
    const payload = {
      employeeId: emp.employeeId,
      employeeName: emp.employeeName,
      centerId: emp.centerId || null,
      role: emp.role || 'employee',
      // Optional admin overlay — when present, sidebar visibility checks
      // see the impersonated user's full picture (role + admin + toggles).
      // When absent, fall back to role-only.
      isAdmin: !!emp.isAdmin,
      permissions: Array.isArray(emp.permissions) ? emp.permissions : [],
      modules: Array.isArray(emp.modules) ? emp.modules : [],
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    setImpersonatedAs(payload);
  }, []);

  const stopImpersonating = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(QA_KEY);
    setImpersonatedAs(null);
    setQaShowHiddenState(false);
  }, []);

  const setQaShowHidden = useCallback((next) => {
    if (next) sessionStorage.setItem(QA_KEY, '1');
    else sessionStorage.removeItem(QA_KEY);
    setQaShowHiddenState(!!next);
  }, []);

  // Synthetic session shaped like a real ops session, populated with the
  // impersonated user's role + admin overlay. Used by Sidebar / MobileNav /
  // StaffRecommend so visibility matches what the impersonated user actually
  // sees — not just their role.
  const impersonatedSession = useMemo(() => {
    if (!impersonatedAs) return null;
    return {
      opsRole: impersonatedAs.role || null,
      opsIsAdmin: !!impersonatedAs.isAdmin,
      opsPermissions: impersonatedAs.permissions || [],
      // canModule() gates on session.modules — omitting this hid the whole
      // sidebar (except Tasks) during View As.
      modules: impersonatedAs.modules || [],
    };
  }, [impersonatedAs]);

  const value = useMemo(() => ({
    impersonatedAs,
    impersonatedSession,
    isImpersonating: !!impersonatedAs,
    canWrite: !impersonatedAs || realIsOwner(),
    impersonatedRole: impersonatedAs?.role || null,
    qaShowHidden: !!impersonatedAs && qaShowHidden,
    setQaShowHidden,
    startImpersonating,
    stopImpersonating,
  }), [impersonatedAs, impersonatedSession, qaShowHidden, setQaShowHidden, startImpersonating, stopImpersonating]);

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
}

export function useImpersonation() {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) throw new Error('useImpersonation must be used inside ImpersonationProvider');
  return ctx;
}

// Merge an impersonation override onto a real session. Swaps display
// identity (employeeId, employeeName, centerId, opsFirstName) plus a
// derived effectiveRole that drives UI gating (sidebar visibility, tab
// visibility). Real ops-auth fields (opsRole, opsUserId, token) are NOT
// touched — backend permissions and write paths still gate on the real
// logged-in user.
export function applyImpersonation(realSession, impersonatedAs) {
  if (!realSession || !impersonatedAs) return realSession;
  const firstName = (impersonatedAs.employeeName || '').split(' ')[0] || null;
  return {
    ...realSession,
    employeeId: impersonatedAs.employeeId,
    employeeName: impersonatedAs.employeeName,
    centerId: impersonatedAs.centerId || realSession.centerId,
    opsFirstName: firstName || realSession.opsFirstName,
    effectiveRole: impersonatedAs.role || realSession.opsRole,
  };
}

// Clears any active impersonation. Call from logout.
export function clearImpersonationStorage() {
  sessionStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(QA_KEY);
}
